import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KnowledgeScoreIndicator } from "@/components/ai-knowledge/KnowledgeScoreIndicator";
import { Building2, Target, Info, AlertCircle, Link2, Unlink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CampaignKnowledgeLinkerProps {
  campaignId: string;
  workspaceId: string;
  disabled?: boolean;
  /** Compact: no own header/tip, cards side by side (used inside settings sections). */
  variant?: "default" | "compact";
}

interface CampaignKnowledge {
  id: string;
  title: string | null;
  knowledge_score: number;
  campaign_goals: any;
  company_profile_id: string | null;
  company_knowledge_profiles: {
    id: string;
    company_name: string;
    knowledge_score: number;
    business_profile: any;
  } | null;
}

interface CompanyProfile {
  id: string;
  company_name: string;
  knowledge_score: number;
  business_profile: any;
}

export function CampaignKnowledgeLinker({
  campaignId,
  workspaceId,
  disabled = false,
  variant = "default",
}: CampaignKnowledgeLinkerProps) {
  const queryClient = useQueryClient();
  const [isLinking, setIsLinking] = useState(false);

  // Fetch currently linked campaign knowledge
  const { data: linkedKnowledge, isLoading: isLoadingLinked } = useQuery({
    queryKey: ["campaign-knowledge-link", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaign_knowledge")
        .select("*, company_knowledge_profiles(*)")
        .eq("campaign_id", campaignId)
        .maybeSingle();

      if (error) throw error;
      return data as CampaignKnowledge | null;
    },
    enabled: !!campaignId,
  });

  // Fetch available campaign strategies (not linked to any campaign)
  const { data: availableStrategies } = useQuery({
    queryKey: ["available-campaign-strategies", workspaceId, campaignId],
    queryFn: async () => {
      // Get strategies that belong to company profiles in this workspace and are not linked to other campaigns
      const { data, error } = await supabase
        .from("campaign_knowledge")
        .select("id, title, knowledge_score, campaign_goals, campaign_id, company_profile_id, company_knowledge_profiles!inner(id, workspace_id, company_name, knowledge_score)")
        .eq("company_knowledge_profiles.workspace_id", workspaceId)
        .or(`campaign_id.is.null,campaign_id.eq.${campaignId}`);

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId,
  });

  // Fetch available business profiles in workspace
  const { data: availableBusinessProfiles } = useQuery({
    queryKey: ["available-business-profiles", workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_knowledge_profiles")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("company_name");

      if (error) throw error;
      return data as CompanyProfile[];
    },
    enabled: !!workspaceId,
  });

  // Mutation to link a campaign strategy
  const linkStrategyMutation = useMutation({
    mutationFn: async (knowledgeId: string) => {
      // First unlink any existing strategy from this campaign
      if (linkedKnowledge?.id && linkedKnowledge.id !== knowledgeId) {
        const { error: unlinkError } = await supabase
          .from("campaign_knowledge")
          .update({ campaign_id: null })
          .eq("id", linkedKnowledge.id);

        if (unlinkError) throw unlinkError;
      }

      // Link the new strategy
      const { error: linkError } = await supabase
        .from("campaign_knowledge")
        .update({ campaign_id: campaignId })
        .eq("id", knowledgeId);

      if (linkError) throw linkError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-knowledge-link", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["available-campaign-strategies", workspaceId] });
      toast.success("Campaign strategy linked successfully");
      setIsLinking(false);
    },
    onError: (error: any) => {
      toast.error("Failed to link strategy: " + error.message);
      setIsLinking(false);
    },
  });

  // Mutation to unlink
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!linkedKnowledge?.id) return;

      const { error } = await supabase
        .from("campaign_knowledge")
        .update({ campaign_id: null })
        .eq("id", linkedKnowledge.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-knowledge-link", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["available-campaign-strategies", workspaceId] });
      toast.success("Knowledge profile unlinked");
    },
    onError: (error: any) => {
      toast.error("Failed to unlink: " + error.message);
    },
  });

  const handleLinkStrategy = (knowledgeId: string) => {
    if (knowledgeId === "none") {
      unlinkMutation.mutate();
    } else {
      linkStrategyMutation.mutate(knowledgeId);
    }
  };

  // Get display info from campaign goals
  const getStrategyInfo = (goals: any) => {
    if (!goals) return { purpose: null, targetAudience: null };
    return {
      purpose: goals.campaign_purpose || goals.purpose || null,
      targetAudience: goals.target_audience || goals.ideal_customer_profile || null,
    };
  };

  const getBusinessInfo = (profile: any) => {
    if (!profile) return { industry: null, description: null };
    return {
      industry: profile.industry || profile.business_type || null,
      description: profile.value_proposition || profile.description || null,
    };
  };

  if (isLoadingLinked) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-32 bg-muted rounded-xl" />
        <div className="h-32 bg-muted rounded-xl" />
      </div>
    );
  }

  const businessProfile = linkedKnowledge?.company_knowledge_profiles;
  const strategyInfo = getStrategyInfo(linkedKnowledge?.campaign_goals);
  const businessInfo = getBusinessInfo(businessProfile?.business_profile);

  return (
    <div className="space-y-6">
      {variant !== "compact" && (
      <div>
        <h2 className="text-lg font-semibold mb-1">AI Knowledge Profiles</h2>
        <p className="text-sm text-muted-foreground">
          Link knowledge profiles to enable AI-powered recommendations for this campaign.
        </p>
      </div>
      )}

      <div className={variant === "compact" ? "grid gap-4 md:grid-cols-2 items-start" : "space-y-6"}>

      {/* Business Profile Card */}
      <Card className={variant === "compact" ? "p-5 space-y-4 rounded-xl border shadow-sm" : "p-6 space-y-4 border"}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">Business profile</h3>
                <Badge variant={businessProfile ? "default" : "outline"} className="h-5 px-1.5 text-[10px] font-medium">{businessProfile ? "Linked" : "Not linked"}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">Company context for AI</p>
            </div>
          </div>
          {businessProfile && (
            <KnowledgeScoreIndicator
              score={businessProfile.knowledge_score}
              variant="compact"
              label="Business Knowledge"
            />
          )}
        </div>

        {businessProfile ? (
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-2">
              <span className="font-medium">{businessProfile.company_name}</span>
              {businessInfo.industry && (
                <Badge variant="secondary" className="text-xs">
                  {businessInfo.industry}
                </Badge>
              )}
            </div>
            {businessInfo.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                "{businessInfo.description}"
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2 px-3 py-2.5 bg-muted/50 rounded-lg">
            <Info className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {linkedKnowledge
                ? "No business profile linked to the campaign strategy"
                : "Link a campaign strategy to see the associated business profile"}
            </span>
          </div>
        )}
      </Card>

      {/* Campaign Strategy Card */}
      <Card className={variant === "compact" ? "p-5 space-y-4 rounded-xl border shadow-sm" : "p-6 space-y-4 border"}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-accent/50 flex items-center justify-center">
              <Target className="h-5 w-5 text-accent-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">Campaign strategy</h3>
                <Badge variant={linkedKnowledge ? "default" : "outline"} className="h-5 px-1.5 text-[10px] font-medium">{linkedKnowledge ? "Linked" : "Not linked"}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">Goals and targeting for AI</p>
            </div>
          </div>
          {linkedKnowledge && (
            <KnowledgeScoreIndicator
              score={linkedKnowledge.knowledge_score}
              variant="compact"
              label="Campaign Knowledge"
            />
          )}
        </div>

        {linkedKnowledge ? (
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {linkedKnowledge.title || "Untitled Strategy"}
                </span>
                {strategyInfo.purpose && (
                  <Badge variant="outline" className="text-xs capitalize">
                    {strategyInfo.purpose}
                  </Badge>
                )}
              </div>
            </div>
            {strategyInfo.targetAudience && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                Target: {strategyInfo.targetAudience}
              </p>
            )}

            {/* Change/Unlink Controls */}
            {!disabled && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <Select
                  value={linkedKnowledge.id}
                  onValueChange={handleLinkStrategy}
                  disabled={linkStrategyMutation.isPending || unlinkMutation.isPending}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Change strategy..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Unlink className="h-3.5 w-3.5" />
                        Unlink profile
                      </div>
                    </SelectItem>
                    {availableStrategies?.map((strategy) => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        <div className="flex items-center gap-2">
                          <span>{strategy.title || "Untitled"}</span>
                          <span className="text-xs text-muted-foreground">
                            ({strategy.knowledge_score}%)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {availableStrategies && availableStrategies.length > 0 ? (
              <>
                <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">
                    No campaign strategy linked. Link one to enable AI recommendations.
                  </span>
                </div>
                {!disabled && (
                  <Select
                    value=""
                    onValueChange={handleLinkStrategy}
                    disabled={linkStrategyMutation.isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a campaign strategy to link..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableStrategies.map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          <div className="flex items-center gap-2">
                            <Link2 className="h-3.5 w-3.5" />
                            <span>{strategy.title || "Untitled Strategy"}</span>
                            <Badge variant="secondary" className="text-[10px]">
                              {strategy.knowledge_score}%
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </>
            ) : (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-muted/50 rounded-lg">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  No strategies yet. Create one in AI Knowledge first.
                </span>
              </div>
            )}
          </div>
        )}
      </Card>
      </div>

      {variant === "compact" ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          Linked profiles give the AI context for messaging and targeting suggestions.
        </p>
      ) : (
      <Alert className="border-primary/20 bg-primary/5">
        <Sparkles className="h-4 w-4 text-primary" />
        <AlertDescription className="text-sm">
          With linked profiles, AI can provide personalized recommendations for messaging, targeting, and campaign optimization based on your business context and campaign goals.
        </AlertDescription>
      </Alert>
      )}
    </div>
  );
}
